import _ from "lodash";
import { Text, useTheme } from "react-native-paper";
import {
  getScenePath,
  Scene,
  scenesQuery,
  sortScenes,
  toScene,
} from "../common/scene";
import React, { ReactNode } from "react";
import {
  DocumentReference,
  DocumentSnapshot,
  QuerySnapshot,
} from "@atgof-firebase/types";
import { SheetCategory, isCharacterCategory } from "../common/sheet";
import { useSheets } from "../data/useSheets";
import { View } from "react-native";
import { transformSnapshot, useSnapshot } from "../data/useSnapshot";
import {
  SceneStatusView,
  intExtDescription,
  setsDescription,
  timeOfDayForScene,
} from "../common/SceneInfo";
import { Phrase, PhraseKey, PhraseLookupFunction } from "../common/phrases";
import { Table } from "./Table";
import {
  TableCellComponent,
  TableRowComponent,
  TableSection,
} from "./types/table";
import { useAvailableApplicands } from "../data/useAvailableApplicands";
import { subjectRef, SubjectSpec } from "../common/subject";
import { useProject } from "../data/useProject";
import { UserInkColour } from "@atgof-common/membership";
import { EditableAttr } from "./EditableAttr";
import { APPLICAND_KINDS, ApplicandKind } from "@atgof-common/applicand";
import { TimeOfDayRepresentation } from "./TimeOfDayRepresentation";

export type ScenesTableSection = TableSection<Scene>;

export type SheetWithApplicands = {
  sheet: DocumentSnapshot;
  looks: DocumentSnapshot[];
  props: DocumentSnapshot[];
};

export type CellRenderProps<T> = {
  item: T;
  scene: Scene;
  category: SheetCategory | undefined;
  character: SubjectSpec | undefined;
};

export type ScenesTableColumn<T> = {
  key: string;
  label: string;
  render: (props: CellRenderProps<T>) => ReactNode;
  backgroundColorForItem?: (item: T, dark: boolean) => string | undefined;
};

type Columns<T> = ScenesTableColumn<T>[];

type ColumnGroupBase = {
  key: string;
  label?: string;
};

export type SceneColumnGroup = ColumnGroupBase & {
  kind: "scene";
  columns: Columns<Scene>;
};

type CharacterColumnGroup = ColumnGroupBase & {
  kind: "character";
  columns: Columns<SubjectSpec>;
};

export type SheetColumnGroup = ColumnGroupBase & {
  kind: { sheetCategory: SheetCategory };
  columns: Columns<SheetWithApplicands>;
};

export type ScenesTableColumnGroup =
  | SceneColumnGroup
  | CharacterColumnGroup
  | SheetColumnGroup;

export function useScenesTableSectionForEpisode(
  episodeRef: DocumentReference | undefined,
  teams: DocumentSnapshot[] | undefined,
) {
  const q = scenesQuery(episodeRef);
  return useSnapshot(
    transformSnapshot<QuerySnapshot, ScenesTableSection[]>(
      q ? _.bind(q.onSnapshot, q) : undefined,
      ({ docs }) => [
        {
          key: episodeRef?.id ?? "",
          entries: sortScenes(docs.map((doc) => toScene(doc, teams))),
        },
      ],
    ),
    [episodeRef?.path, teams],
  );
}

export function sceneColumn(
  ph: PhraseLookupFunction,
  extras?: (scene: Scene) => React.ReactNode,
): ScenesTableColumn<Scene> {
  return {
    key: "scene",
    label: ph("scene") as string,
    backgroundColorForItem: (scene, dark) =>
      dark ? undefined : scene.teamData?.backgroundColour,
    render: ({ item }) => {
      const { dark } = useTheme();
      return (
        <View>
          <SceneStatusView
            scene={item}
            excludeTimeOfDay
            fontSize={null}
            color={dark ? item.teamData?.backgroundColour : undefined}
            sceneLabelSeparator=">"
          />
          <View
            style={{
              flexDirection: "row",
              flexWrap: "wrap",
              overflow: "hidden",
            }}
          >
            {extras ? extras(item) : null}
          </View>
        </View>
      );
    },
  };
}

export function sceneNotesColumn(
  ph: PhraseLookupFunction,
  inkColours: UserInkColour[] | undefined,
) {
  return {
    key: "scene-notes",
    label: ph("scene-notes") as string,
    render: ({ item }: CellRenderProps<Scene>) => (
      <EditableAttr
        attr="paNotes"
        refOrSnapshot={item.ref}
        inkColours={inkColours ?? []}
      />
    ),
  };
}

export function locationColumn(
  ph: (k: PhraseKey) => Phrase,
): ScenesTableColumn<Scene> {
  return {
    key: "location",
    label: ph("location") as string,
    render: ({ item }) => (
      <React.Fragment>
        <View style={{ flexDirection: "row", gap: 4, alignItems: "center" }}>
          <Text style={{ fontWeight: "bold", maxWidth: 400 }} numberOfLines={1}>
            {setsDescription(item)}
          </Text>
          <TimeOfDayRepresentation timeOfDay={item.timeOfDay} />
        </View>
        <View style={{ flexDirection: "row", gap: 4, alignItems: "center" }}>
          <Text>{intExtDescription(item, ph).toLocaleUpperCase()}</Text>
          <Text>{timeOfDayForScene(item)}</Text>
        </View>
        <Text variant="bodySmall">{item.synopsis}</Text>
        <Text variant="bodySmall">{item.director}</Text>
      </React.Fragment>
    ),
  };
}

function Cells({
  columnGroup,
  scene,
  character,
  rowIndex,
  groupIndex,
  rowCount,
  TableCell,
}: {
  columnGroup: ScenesTableColumnGroup;
  scene: Scene;
  character: SubjectSpec | undefined;
  rowIndex: number;
  groupIndex: number;
  rowCount: number;
  TableCell: TableCellComponent;
}) {
  const { dark } = useTheme();
  const { key, kind, columns } = columnGroup;
  const sheetCategory =
    typeof kind === "object" ? kind.sheetCategory : undefined;
  const perScene = kind === "scene";
  const perCharacter =
    kind == "character" || (!perScene && isCharacterCategory(sheetCategory));
  const item = perScene ? scene : kind === "character" ? character : undefined;
  const characterSubjectSpec: SubjectSpec | undefined = perCharacter
    ? character
    : undefined;
  const sheets = useSheets(
    sheetCategory ? scene.ref : undefined,
    typeof kind === "object" ? [kind.sheetCategory] : [],
    characterSubjectSpec,
  );
  const loading = !(item || sheets);
  const firstSheet = sheets?.length ? sheets[0] : undefined;
  const sheetSet: string | undefined =
    firstSheet && firstSheet.get("category") === "arts"
      ? firstSheet.get("set")
      : undefined;
  const project = useProject();
  const subjectSpec: SubjectSpec | undefined =
    characterSubjectSpec ??
    (sheetSet ? { subjectKind: "set", subjectName: sheetSet } : undefined);
  const [looks, props] = APPLICAND_KINDS.map((kind: ApplicandKind) =>
    useAvailableApplicands(
      kind,
      subjectSpec ? subjectRef(project, subjectSpec) : undefined,
      [getScenePath(scene.ref)],
      sheetCategory ? [sheetCategory] : [],
    ),
  );
  const isFirstCharacter = rowIndex == 0;
  const toRender = item
    ? [item]
    : sheets?.length
      ? sheets.map((sheet) => ({
          sheet,
          looks:
            looks?.filter((look) =>
              sheet
                ?.get("looks")
                ?.find((ref: DocumentReference) => ref.path === look.ref.path),
            ) ?? [],
          props:
            props?.filter((prop) =>
              sheet
                ?.get("props")
                ?.find((ref: DocumentReference) => ref.path === prop.ref.path),
            ) ?? [],
        }))
      : sheetCategory && !loading
        ? [undefined]
        : [];
  return perCharacter || isFirstCharacter
    ? columns.map((column, colI) => {
        return (
          <TableCell
            key={key + " " + column.key}
            loading={loading}
            rowIndex={rowIndex}
            groupIndex={groupIndex}
            columnIndex={colI}
            rowSpan={perCharacter ? 1 : rowCount || 1}
            backgroundColor={
              toRender.length &&
              toRender[0] !== undefined &&
              column.backgroundColorForItem
                ? column.backgroundColorForItem(toRender[0] as any, dark)
                : undefined
            }
          >
            <View
              style={{
                flex: 1,
                height: "100%",
                alignSelf: "flex-start",
                padding: 8,
              }}
            >
              {toRender.map((item, i) =>
                React.createElement(
                  column.render as any, // TODO
                  {
                    key: i,
                    item: item as any, // TODO
                    scene,
                    category: sheetCategory,
                    character: perCharacter ? character : undefined,
                  },
                ),
              )}
            </View>
          </TableCell>
        );
      })
    : [];
}

function SceneRow({
  entry,
  columnGroups,
  TableRow,
  TableCell,
}: {
  entry: Scene;
  columnGroups: ScenesTableColumnGroup[];
  TableRow: TableRowComponent;
  TableCell: TableCellComponent;
}) {
  const { cast, nonCast } = entry;
  const rows = [
    ...(cast ?? []).map(
      (subjectName): SubjectSpec => ({
        subjectKind: "character",
        subjectName,
        performerKind: "cast",
      }),
    ),
    ...(nonCast?.sa ?? []).map(
      (subjectName): SubjectSpec => ({
        subjectKind: "character",
        subjectName,
        performerKind: "sa",
      }),
    ),
  ];
  return (rows.length ? rows : [undefined]).map((character, rowI) => (
    <TableRow key={character?.subjectName ?? ""}>
      {columnGroups.map((columnGroup, groupI) => (
        <Cells
          key={columnGroup.key}
          columnGroup={columnGroup}
          scene={entry}
          character={character}
          rowIndex={rowI}
          groupIndex={groupI}
          rowCount={rows.length}
          TableCell={TableCell}
        />
      ))}
    </TableRow>
  ));
}

function columnGroupOrder({ kind }: ScenesTableColumnGroup) {
  return kind === "scene"
    ? 0
    : kind === "character"
      ? 1
      : isCharacterCategory(kind.sheetCategory)
        ? 2
        : 3;
}

const coreColumns = ["scene", "location", "character"];

export function ScenesTable({
  sections,
  ...props
}: {
  sections: ScenesTableSection[];
  columnGroups: ScenesTableColumnGroup[];
}) {
  const columnGroups = props.columnGroups
    .filter(({ columns }) => columns.length)
    .sort((a, b) => columnGroupOrder(a) - columnGroupOrder(b));
  const totalCols =
    coreColumns.length +
    columnGroups
      .map(({ columns }) => columns.length)
      .reduce((a, b) => a + b, 0);
  return (
    <Table<Scene, ScenesTableColumnGroup>
      columnGroups={columnGroups}
      sections={sections}
      totalCols={totalCols}
      Row={SceneRow}
    />
  );
}
